<script setup lang="ts">
import { ref, watch } from 'vue'
import type { ApiClient } from '@xjlv2/api-client'
import type { PushApp, PushTemplate } from './types'
import { pushAppOptions } from './types'
import { formatTime, statusText } from './usePushCenterState'
import { Check, Copy, Plus } from '@lucide/vue'
import {
  AdminDataTable,
  AdminDialog,
  AdminFilterBar,
  AdminStatusTag,
  RrButton,
  RrCheckbox,
  RrInput,
  RrSelect,
  RrTag,
  type TableColumnType
} from '@xjlv2/admin-layout'

const props = withDefaults(
  defineProps<{
    templates?: PushTemplate[]
    total: number
    page: number
    pageSize: number
    keyword: string
    enabledFilter: string
    queryLoading?: boolean
    apps?: PushApp[]
    appCode?: string
    canManage?: boolean
    createApiClient: () => ApiClient
  }>(),
  {
    templates: () => [],
    apps: () => [],
    appCode: '',
    canManage: false,
    queryLoading: false
  }
)

const emit = defineEmits<{
  'update:appCode': [code: string]
  refresh: []
  search: [keyword: string, enabled: string, appCode: string]
  'page-change': [pageNo: number, pageSize: number]
  notice: [msg: string]
  error: [msg: string]
}>()

const localAppCode = ref(props.appCode)
watch(
  () => props.appCode,
  (code) => {
    localAppCode.value = code
  }
)

function onAppChange(code: string) {
  emit('update:appCode', code)
}

const keyword = ref(props.keyword)
const statusFilter = ref<string>(props.enabledFilter || 'ALL')
watch(
  () => props.keyword,
  (value) => {
    keyword.value = value
  }
)
watch(
  () => props.enabledFilter,
  (value) => {
    statusFilter.value = value || 'ALL'
  }
)
const dialogVisible = ref(false)
const copiedKey = ref<string | null>(null)
const loading = ref(false)

const columns: TableColumnType<PushTemplate>[] = [
  { key: 'appCode', title: '应用', width: 120 },
  { key: 'templateCode', title: '模板编码', width: 180 },
  { key: 'templateName', title: '模板名称', width: 180 },
  { key: 'content', title: '标题与内容模板', minWidth: 240 },
  { key: 'status', title: '状态', width: 100 },
  { key: 'updatedAt', title: '更新时间', width: 180 },
  { key: 'actions', title: '操作', width: 100, align: 'right', fixed: 'right' }
]

const form = ref({
  id: null as number | null,
  appCode: props.appCode,
  templateCode: '',
  templateName: '',
  titleTemplate: '',
  bodyTemplate: '',
  dataTemplate: '{}',
  enabled: true
})

function handleSearch() {
  emit(
    'search',
    keyword.value.trim(),
    statusFilter.value === 'ALL' ? '' : statusFilter.value === 'ENABLED' ? 'true' : 'false',
    localAppCode.value
  )
}

function handleReset() {
  keyword.value = ''
  statusFilter.value = 'ALL'
  localAppCode.value = ''
  emit('update:appCode', '')
  emit('search', '', '', '')
}

function openDialog(tpl?: PushTemplate) {
  if (tpl) {
    form.value = {
      id: tpl.id,
      appCode: tpl.appCode,
      templateCode: tpl.templateCode,
      templateName: tpl.templateName,
      titleTemplate: tpl.titleTemplate,
      bodyTemplate: tpl.bodyTemplate,
      dataTemplate: tpl.dataTemplateJson || '{}',
      enabled: tpl.enabled
    }
  } else {
    form.value = {
      id: null,
      appCode: localAppCode.value || props.appCode,
      templateCode: '',
      templateName: '',
      titleTemplate: '',
      bodyTemplate: '',
      dataTemplate: '{}',
      enabled: true
    }
  }
  dialogVisible.value = true
}

async function save() {
  if (!props.canManage) return
  loading.value = true
  try {
    const api = props.createApiClient()
    const dataTemplate = JSON.parse(form.value.dataTemplate || '{}') as Record<string, string>
    const payload = {
      appCode: form.value.appCode,
      templateCode: form.value.templateCode,
      templateName: form.value.templateName,
      titleTemplate: form.value.titleTemplate,
      bodyTemplate: form.value.bodyTemplate,
      dataTemplate,
      enabled: form.value.enabled
    }
    if (form.value.id) {
      await api.put(
        `/api/saas/push/templates/${encodeURIComponent(form.value.appCode)}/${encodeURIComponent(form.value.templateCode)}`,
        {
          templateName: payload.templateName,
          titleTemplate: payload.titleTemplate,
          bodyTemplate: payload.bodyTemplate,
          dataTemplate: payload.dataTemplate,
          enabled: payload.enabled
        }
      )
    } else {
      await api.post('/api/saas/push/templates', payload)
    }
    emit('notice', `消息模板 ${form.value.templateName} 已保存`)
    dialogVisible.value = false
    emit('refresh')
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '模板保存失败')
  } finally {
    loading.value = false
  }
}

function copyToClipboard(text: string, key: string) {
  if (!text) return
  void navigator.clipboard.writeText(text).then(() => {
    copiedKey.value = key
    setTimeout(() => {
      copiedKey.value = null
    }, 1500)
    emit('notice', `已复制: ${text}`)
  })
}

function insertVar(v: string) {
  form.value.bodyTemplate = `${form.value.bodyTemplate}\${${v}}`
}
</script>

<template>
  <div class="push-tab-pane">
    <AdminFilterBar :loading="loading" @submit="handleSearch" @reset="handleReset">
      <label>
        所属应用
        <RrSelect
          v-model:value="localAppCode"
          :options="pushAppOptions(apps)"
          @change="onAppChange"
        />
      </label>
      <label>
        关键字
        <RrInput
          v-model:value="keyword"
          placeholder="模板名称 / 编码 / 标题"
          @keyup.enter="handleSearch"
        />
      </label>
      <label>
        状态
        <RrSelect
          v-model:value="statusFilter"
          :options="[
            { label: '全部状态', value: 'ALL' },
            { label: '已启用', value: 'ENABLED' },
            { label: '已停用', value: 'DISABLED' }
          ]"
          @change="handleSearch"
        />
      </label>
      <template #actions>
        <RrButton type="primary" :disabled="!canManage" @click="openDialog()">
          <template #icon><Plus :size="16" /></template>
          新建模板
        </RrButton>
      </template>
    </AdminFilterBar>

    <AdminDataTable
      :columns="columns"
      :data-source="templates"
      :loading="queryLoading"
      :current="page"
      :page-size="pageSize"
      :total="total"
      :empty="templates.length === 0"
      empty-title="暂无消息模板"
      empty-description="可点击上方「新建模板」按钮进行创建。"
      @update:current="(p) => emit('page-change', p, pageSize)"
      @update:page-size="(size) => emit('page-change', 1, size)"
      @refresh="handleSearch"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'appCode'">
          <RrTag tone="neutral" :label="record.appCode" />
        </template>
        <template v-else-if="column.key === 'templateCode'">
          <div class="code-copy-cell">
            <code>{{ record.templateCode }}</code>
            <button
              type="button"
              class="mini-copy-btn"
              title="复制模板编码"
              @click="copyToClipboard(record.templateCode, `tpl-${record.id}`)"
            >
              <Check v-if="copiedKey === `tpl-${record.id}`" :size="12" class="is-success" />
              <Copy v-else :size="12" />
            </button>
          </div>
        </template>
        <template v-else-if="column.key === 'templateName'">
          <strong>{{ record.templateName }}</strong>
        </template>
        <template v-else-if="column.key === 'content'">
          <div class="template-content-cell">
            <span class="tpl-title">{{ record.titleTemplate || '-' }}</span>
            <small class="tpl-body">{{ record.bodyTemplate || '-' }}</small>
          </div>
        </template>
        <template v-else-if="column.key === 'status'">
          <AdminStatusTag
            :text="statusText(record.enabled)"
            :tone="record.enabled ? 'success' : 'neutral'"
          />
        </template>
        <template v-else-if="column.key === 'updatedAt'">
          <span class="text-time">{{ formatTime(record.updatedAt) }}</span>
        </template>
        <template v-else-if="column.key === 'actions'">
          <RrButton html-type="button" class="secondary compact-btn" @click="openDialog(record)">
            编辑
          </RrButton>
        </template>
      </template>
    </AdminDataTable>

    <!-- 编辑消息模板标准弹窗 -->
    <AdminDialog
      v-model:open="dialogVisible"
      :title="form.id ? '编辑消息模板' : '新建消息模板'"
      :width="640"
      :confirm-loading="loading"
      ok-text="保存模板"
      @ok="save"
      @cancel="dialogVisible = false"
    >
      <div class="modal-form-grid">
        <div class="form-row-2">
          <label class="form-field">
            <span class="field-label required">应用编码</span>
            <RrInput
              v-model:value.trim="form.appCode"
              placeholder="例如：app-portal"
              autocomplete="off"
            />
          </label>
          <label class="form-field">
            <span class="field-label required">模板编码</span>
            <RrInput
              v-model:value.trim="form.templateCode"
              placeholder="例如：daily_sign_reward"
              autocomplete="off"
            />
          </label>
        </div>

        <div class="form-row-2">
          <label class="form-field">
            <span class="field-label required">模板名称</span>
            <RrInput
              v-model:value.trim="form.templateName"
              placeholder="例如：日签星豆到账"
              autocomplete="off"
            />
          </label>
          <label class="form-field checkbox-field">
            <span class="field-label">启用状态</span>
            <div class="checkbox-box">
              <RrCheckbox v-model:checked="form.enabled" />
              <span>{{ form.enabled ? '启用模板' : '停用模板' }}</span>
            </div>
          </label>
        </div>

        <label class="form-field">
          <span class="field-label required">标题模板</span>
          <RrInput
            v-model:value.trim="form.titleTemplate"
            placeholder="例如：星豆到账通知"
            autocomplete="off"
          />
        </label>

        <label class="form-field">
          <span class="field-label required">内容模板</span>
          <RrInput
            v-model:value="form.bodyTemplate"
            type="textarea"
            :rows="3"
            placeholder="例如：${nickname} 完成日签，获得 ${points} 星豆"
          />
          <div class="param-tags-strip">
            <span class="strip-tip">快捷插入参数：</span>
            <button type="button" class="param-chip" @click="insertVar('nickname')">
              ${nickname}
            </button>
            <button type="button" class="param-chip" @click="insertVar('points')">${points}</button>
            <button type="button" class="param-chip" @click="insertVar('time')">${time}</button>
            <button type="button" class="param-chip" @click="insertVar('orderNo')">
              ${orderNo}
            </button>
          </div>
        </label>

        <label class="form-field">
          <span class="field-label">扩展参数 (Data JSON)</span>
          <RrInput
            v-model:value="form.dataTemplate"
            type="textarea"
            :rows="3"
            class="code-font"
            placeholder="{}"
          />
        </label>
      </div>
    </AdminDialog>
  </div>
</template>

<style scoped>
.push-tab-pane {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-4);
}

.code-copy-cell {
  display: inline-flex;
  align-items: center;
  gap: var(--rr-space-2);
}

.code-copy-cell code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: var(--rr-font-size-sm);
  color: var(--rr-color-text);
  background: var(--rr-color-canvas);
  padding: var(--rr-space-1) var(--rr-space-2);
  border-radius: var(--rr-radius-xs);
}

.mini-copy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--rr-space-6);
  height: var(--rr-space-6);
  border: 0;
  background: transparent;
  color: var(--rr-color-text-muted);
  border-radius: var(--rr-radius-xs);
  cursor: pointer;
}

.mini-copy-btn:hover {
  background: var(--rr-color-row-hover);
  color: var(--rr-color-text-strong);
}

.mini-copy-btn .is-success {
  color: var(--rr-color-success);
}

.template-content-cell {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-1);
  max-width: 480px;
}

.tpl-title {
  font-weight: var(--rr-font-weight-bold);
  color: var(--rr-color-text-strong);
  font-size: var(--rr-font-size-base);
}

.tpl-body {
  color: var(--rr-color-text-muted);
  font-size: var(--rr-font-size-sm);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: var(--rr-line-height-base);
}

.text-time {
  font-size: var(--rr-font-size-sm);
  color: var(--rr-color-text-muted);
}

.compact-btn {
  padding: var(--rr-space-1) var(--rr-space-2);
  font-size: var(--rr-font-size-sm);
}

.modal-form-grid {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-4);
  padding: var(--rr-space-2) 0;
}

.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--rr-space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-2);
}

.field-label {
  font-size: var(--rr-font-size-base);
  font-weight: var(--rr-font-weight-bold);
  color: var(--rr-color-text);
}

.field-label.required::after {
  content: ' *';
  color: var(--rr-color-danger);
}

.code-font :deep(.rr-textarea) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.checkbox-field {
  justify-content: flex-end;
}

.checkbox-box {
  display: flex;
  align-items: center;
  gap: var(--rr-space-2);
  min-height: var(--rr-space-8);
  font-size: var(--rr-font-size-base);
  color: var(--rr-color-text);
}

.param-tags-strip {
  display: flex;
  align-items: center;
  gap: var(--rr-space-2);
  flex-wrap: wrap;
  margin-top: var(--rr-space-1);
}

.strip-tip {
  font-size: var(--rr-font-size-xs);
  color: var(--rr-color-text-muted);
}

.param-chip {
  padding: var(--rr-space-1) var(--rr-space-2);
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-xs);
  background: var(--rr-color-canvas);
  color: var(--rr-color-text);
  font-size: var(--rr-font-size-xs);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  cursor: pointer;
}

.param-chip:hover {
  background: var(--rr-color-row-hover);
  border-color: var(--rr-color-primary-hover);
  color: var(--rr-color-primary-hover);
}
</style>
